// src/services/apiError.ts
//
// THE single place that knows the wire shape of an API error body. No other
// module in www may parse `error` / `errors` / `message` off a response body
// directly — call readApiError() or getFieldErrors() instead.
//
// Pure module: no import of axios, @/app/store or react-toastify, so it is
// unit-testable with plain object literals (see apiError.test.ts).

/** elemis error types — argfin/elemis developers/src/content/errors.md, mapped in
 *  ApiErrorRenderer::typeForStatus() (common-package). Not a closed union at runtime:
 *  the string fallback keeps an unknown future type readable rather than throwing. */
export type ApiErrorType =
    | 'authentication_error'
    | 'permission_error'
    | 'invalid_request_error'
    | 'rate_limit_error'
    | 'api_error';

export interface ApiErrorEnvelope {
    type: ApiErrorType | (string & {});
    code?: string;
    message: string;
    param?: string;
    /** D1 = B: error.errors is the field map's permanent home since slice 3a. */
    errors?: Record<string, string[]>;
}

/** A union, deliberately not `unknown`: no producer emits a string today, and
 *  reading it without narrowing must still fail tsc. */
export interface ApiErrorResponse {
    error?: ApiErrorEnvelope | string;
    message?: string | string[];
    errors?: Record<string, string[]>;
    data?: Record<string, unknown>;
    success?: boolean;
}

export interface NormalisedApiError {
    message: string; // never empty — falls back to DEFAULT_ERROR_MESSAGE
    type?: ApiErrorType | string;
    code?: string;
    param?: string;
}

export const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred';

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0;
}

function isFieldErrorsShape(value: unknown): value is Record<string, string[]> {
    if (!isPlainObject(value)) return false;
    return Object.values(value).every(
        (v) => Array.isArray(v) && v.every((item) => typeof item === 'string'),
    );
}

/** A `responseType: 'blob'` request delivers its error body as a Blob. Read it as JSON so the
 *  rest of this module sees the same body as for any other request; a body that is empty or
 *  not JSON becomes undefined. Anything that is not a Blob is returned unchanged. */
export async function readBlobBody(body: unknown): Promise<unknown> {
    if (!(body instanceof Blob)) return body;

    try {
        return JSON.parse(await body.text());
    } catch {
        return undefined;
    }
}

/** Never throws and never returns an empty message, for any input.
 *  Wire shapes and precedence: docs/features/api-error-envelope.md. */
export function readApiError(body: unknown): NormalisedApiError {
    if (!isPlainObject(body)) {
        return { message: DEFAULT_ERROR_MESSAGE };
    }

    const response = body as ApiErrorResponse;
    const envelope = isPlainObject(response.error)
        ? (response.error as ApiErrorEnvelope)
        : undefined;

    // type/code/param are read from body.error only when it is an object; undefined
    // otherwise. Populated regardless of which message rank ultimately wins.
    const type = envelope && typeof envelope.type === 'string' ? envelope.type : undefined;
    const code = envelope && typeof envelope.code === 'string' ? envelope.code : undefined;
    const param = envelope && typeof envelope.param === 'string' ? envelope.param : undefined;

    if (envelope && isNonEmptyString(envelope.message)) {
        return { message: envelope.message, type, code, param };
    }

    // Permanent, not a compat shim: hundreds of {success, message, data} bodies
    // carry no envelope at all.
    if (isNonEmptyString(response.message)) {
        return { message: response.message, type, code, param };
    }
    if (Array.isArray(response.message) && response.message.length > 0) {
        return { message: response.message.join(', '), type, code, param };
    }

    return { message: DEFAULT_ERROR_MESSAGE, type, code, param };
}

// Accepts an ApiError-like `{data}`, an AxiosError-like `{response: {data}}`, or a
// raw body. Tries `err.data` first, then `err.response?.data`, then falls back to
// treating the source itself as the body — this is what keeps hand-rolled test
// fixtures (which construct any one of the three shapes) valid unchanged (D4).
function extractBody(source: unknown): unknown {
    if (!isPlainObject(source)) {
        return source;
    }
    const withData = source as { data?: unknown; response?: { data?: unknown } };
    if (withData.data !== undefined) {
        return withData.data;
    }
    if (isPlainObject(withData.response) && withData.response.data !== undefined) {
        return withData.response.data;
    }
    return source;
}

/** Dual read: error.errors (the field map's permanent home since 3a), then the
 *  top-level errors fallback that #848-F4's hand-rolled bodies still require.
 *  Accepts ApiError, AxiosError, or a raw body. Always returns an object,
 *  never undefined. Entries failing the Record<string,string[]> shape are dropped
 *  (the whole map, not per-field — a body where `errors` is a string, or a field
 *  maps to a string rather than an array, yields `{}` rather than a runtime
 *  `.forEach is not a function` inside a `setError` loop). */
export function getFieldErrors(source: unknown): Record<string, string[]> {
    const body = extractBody(source);
    if (!isPlainObject(body)) {
        return {};
    }
    const response = body as ApiErrorResponse;
    const envelope = isPlainObject(response.error)
        ? (response.error as ApiErrorEnvelope)
        : undefined;

    if (envelope && isFieldErrorsShape(envelope.errors)) {
        return envelope.errors;
    }
    if (isFieldErrorsShape(response.errors)) {
        return response.errors;
    }
    return {};
}

export interface VersionConflict {
    currentVersion: number;
    status: string | undefined;
}

/** Reads `current_version` and `status` from a `quote_version_conflict` error body; null otherwise. */
export function readVersionConflict(source: unknown): VersionConflict | null {
    const body = extractBody(source);
    if (!isPlainObject(body) || !isPlainObject(body.error)) {
        return null;
    }
    const { current_version: currentVersion, status } = body.error;
    if (typeof currentVersion !== 'number' || !Number.isInteger(currentVersion)) {
        return null;
    }
    return { currentVersion, status: typeof status === 'string' ? status : undefined };
}

export type QuoteTerminalStatus = 'completed' | 'dead';

/** Reads the status of a quote that refused a write because it is already completed or dead; null otherwise. */
export function readQuoteStatusRefusal(source: unknown): QuoteTerminalStatus | null {
    if (!isPlainObject(source) || source.status !== 422) {
        return null;
    }
    const body = extractBody(source);
    if (!isPlainObject(body) || 'errors' in body || !isPlainObject(body.data)) {
        return null;
    }
    const { status } = body.data;
    return status === 'completed' || status === 'dead' ? status : null;
}
